import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Mail, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const t = {
  en: {
    title: "TK Assistant",
    subtitle: "Ask me anything about the conference",
    placeholder: "Type your question...",
    send: "Send",
    greeting: "Hi! 👋 I'm TK Assistant. I can help you with registration, payment, visa letters, the schedule, speakers, and more. What can I do for you?",
    emailHelp: "Email Support",
    errorReply: "Sorry, I couldn't reach the assistant right now. Please try again or email us.",
    poweredBy: "Responses are AI-generated",
  },
  fr: {
    title: "TK Assistant",
    subtitle: "Posez-moi toutes vos questions sur la conférence",
    placeholder: "Posez votre question...",
    send: "Envoyer",
    greeting: "Bonjour ! 👋 Je suis TK Assistant. Je peux vous aider avec l'inscription, le paiement, les lettres d'invitation visa, le programme, les conférenciers et plus encore. Comment puis-je vous aider ?",
    emailHelp: "Contacter par Courriel",
    errorReply: "Désolée, je n'arrive pas à joindre l'assistant pour le moment. Veuillez réessayer ou nous envoyer un courriel.",
    poweredBy: "Réponses générées par IA",
  },
};

function getOrCreateSessionId() {
  let sid = sessionStorage.getItem('tk_chat_session_id');
  if (!sid) {
    sid = `tk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem('tk_chat_session_id', sid);
  }
  return sid;
}

export default function ChatBot({ language = 'en', token }) {
  const lang = (language || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en';
  const labels = t[lang];

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(getOrCreateSessionId());

  // Load persisted messages (session only)
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('tk_chat_messages');
      if (stored) {
        setMessages(JSON.parse(stored));
      } else {
        setMessages([{ role: 'assistant', content: labels.greeting }]);
      }
    } catch (e) {
      setMessages([{ role: 'assistant', content: labels.greeting }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If language changes and the greeting was auto-set, refresh it
  useEffect(() => {
    if (messages.length === 1 && messages[0].role === 'assistant') {
      setMessages([{ role: 'assistant', content: labels.greeting }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Persist messages in sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('tk_chat_messages', JSON.stringify(messages));
    } catch (e) { /* ignore quota */ }
  }, [messages]);

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, loading]);

  // Focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const history = messages.filter(m => m.role === 'user' || m.role === 'assistant');
    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/chat/message`,
        { session_id: sessionId.current, language: lang, message: text, history },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages([...nextMessages, { role: 'assistant', content: res.data.reply }]);
    } catch (e) {
      setMessages([...nextMessages, { role: 'assistant', content: labels.errorReply, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating launcher button */}
      {!open && (
        <button
          data-testid="chatbot-launcher"
          onClick={() => setOpen(true)}
          aria-label={labels.title}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#CD0000] hover:bg-[#A00000] text-white rounded-full shadow-xl shadow-pink-500/30 px-5 py-3.5 font-bold text-sm transition-all hover:scale-105 active:scale-95"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="hidden sm:inline">{labels.title}</span>
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[400px] h-[75vh] max-h-[640px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#1D1D1D] text-white">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#CD0000]/20 border-2 border-[#CD0000] flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-[#CD0000]" />
              </div>
              <div>
                <p className="font-extrabold text-sm">{labels.title}</p>
                <p className="text-xs text-white/60">{labels.subtitle}</p>
              </div>
            </div>
            <button
              data-testid="chatbot-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chatbot-msg-${m.role}`}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-[#CD0000] text-white rounded-br-sm'
                      : m.error
                      ? 'bg-red-50 border border-red-200 text-red-800 rounded-bl-sm'
                      : 'bg-white border border-gray-200 text-[#1D1D1D] rounded-bl-sm'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="chatbot-loading">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2 text-[#1D1D1D]">
                  <Loader2 className="w-4 h-4 animate-spin text-[#CD0000]" />
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Email handoff bar */}
          <a
            href="mailto:ottawa@talithakoumitug.com?subject=TK-TUG Conference Question"
            data-testid="chatbot-email-handoff"
            className="mx-4 mb-2 mt-0 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-red-50 to-purple-50 border border-red-100 text-xs font-semibold text-[#CD0000] hover:bg-red-100 transition-colors"
          >
            <Mail className="w-3.5 h-3.5" />
            {labels.emailHelp}: ottawa@talithakoumitug.com
          </a>

          {/* Input */}
          <div className="px-4 pb-4 pt-1 bg-white border-t border-gray-100">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={labels.placeholder}
                rows={1}
                maxLength={2000}
                className="flex-1 resize-none border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#CD0000]/40 focus:border-[#CD0000] max-h-24"
              />
              <button
                data-testid="chatbot-send"
                onClick={send}
                disabled={!input.trim() || loading}
                aria-label={labels.send}
                className="shrink-0 w-10 h-10 rounded-xl bg-[#CD0000] hover:bg-[#A00000] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-gray-400 text-center mt-1.5">{labels.poweredBy}</p>
          </div>
        </div>
      )}
    </>
  );
}
